import React from "react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { AlertTriangle } from "lucide-react";

export default function Disclaimer() {
  return (
    <div className="min-h-screen flex flex-col bg-background" data-testid="disclaimer-page">
      <Header />
      <main className="flex-1">
        <section className="bg-primary text-white">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 py-12 md:py-14">
            <p className="text-accent text-sm font-semibold tracking-wider uppercase mb-3">Legal</p>
            <h1 className="font-display text-4xl md:text-5xl font-bold">Disclaimer</h1>
            <p className="text-white/70 mt-3 text-sm">Last updated: January 2026</p>
          </div>
        </section>

        <article className="max-w-4xl mx-auto px-4 sm:px-6 py-10 md:py-14 prose prose-slate max-w-none">
          <div className="not-prose flex items-start gap-3 p-4 rounded-xl border border-amber-200 bg-amber-50 my-4">
            <AlertTriangle className="h-5 w-5 text-amber-700 mt-0.5 shrink-0" />
            <p className="text-sm text-ink/80 m-0">
              The Pataudi is a <strong>listings marketplace</strong>. We introduce buyers to sellers — we are not a party
              to the deal, we don&apos;t hold inventory, we don&apos;t inspect the property or vehicle, and we don&apos;t
              take a cut of the transaction between you and the seller.
            </p>
          </div>

          <h2 className="font-display text-2xl font-bold text-ink mt-8 mb-3">1. Listings are user-submitted</h2>
          <p className="text-ink/80 leading-relaxed">
            Titles, prices, photos, specifications and contact details on every listing are provided by the person or
            business posting it. We check for spam, banned categories and obvious fraud before approving, but we cannot
            physically verify each property, vehicle, service or business.
          </p>

          <h2 className="font-display text-2xl font-bold text-ink mt-8 mb-3">2. Do your own due diligence</h2>
          <ul className="list-disc pl-6 text-ink/80 space-y-1.5">
            <li>Visit the property and check documents (title deed, RERA number, khasra, tax receipts) before paying anything.</li>
            <li>Take a test drive and verify RC, insurance, PUC, and chassis number before buying a vehicle.</li>
            <li>Confirm licenses, GST and reviews before hiring a business or service provider.</li>
            <li>Prefer in-person meetings at a public location; use bank transfer only after physical verification.</li>
          </ul>

          <h2 className="font-display text-2xl font-bold text-ink mt-8 mb-3">3. Pricing is indicative</h2>
          <p className="text-ink/80 leading-relaxed">
            Prices shown on listings are what the seller expects and are almost always negotiable. Government fees,
            stamp duty, registration charges, brokerage (if any), and taxes are separate and are the buyer&apos;s
            responsibility unless the seller explicitly states otherwise in the listing.
          </p>

          <h2 className="font-display text-2xl font-bold text-ink mt-8 mb-3">4. No professional advice</h2>
          <p className="text-ink/80 leading-relaxed">
            Content on The Pataudi — including area guides, loan calculators, and category tips — is for general
            information only. It is not legal, financial, tax, valuation or investment advice. Please consult a licensed
            professional before signing agreements or transferring large sums.
          </p>

          <h2 className="font-display text-2xl font-bold text-ink mt-8 mb-3">5. External links</h2>
          <p className="text-ink/80 leading-relaxed">
            Some listings and articles link to third-party sites such as Google Maps, WhatsApp, bank portals or partner
            developers. We don&apos;t control those websites and are not responsible for their content, policies, or
            availability.
          </p>

          <h2 className="font-display text-2xl font-bold text-ink mt-8 mb-3">6. Availability of the platform</h2>
          <p className="text-ink/80 leading-relaxed">
            We do our best to keep the site up 24×7, but we may pause the service for maintenance, upgrades, or reasons
            outside our control (internet outages, DDoS attacks, government orders). Extended downtime during a paid boost
            is credited back — see the <a href="/refund" className="text-primary underline">Refund Policy</a>.
          </p>

          <h2 className="font-display text-2xl font-bold text-ink mt-8 mb-3">7. Limitation of liability</h2>
          <p className="text-ink/80 leading-relaxed">
            To the fullest extent allowed by Indian law, The Pataudi, its founders and its team are not liable for any
            loss, damage, injury or dispute arising out of a transaction between a buyer and a seller you met through the
            platform. Our aggregate liability for any claim directly against us is capped at the amount you paid The
            Pataudi in the twelve months before the claim.
          </p>

          <h2 className="font-display text-2xl font-bold text-ink mt-8 mb-3">8. Governing law</h2>
          <p className="text-ink/80 leading-relaxed">
            This Disclaimer is governed by the laws of India. Any dispute is subject to the exclusive jurisdiction of the
            courts in Gurugram, Haryana.
          </p>

          <h2 className="font-display text-2xl font-bold text-ink mt-8 mb-3">9. Report a problem</h2>
          <p className="text-ink/80 leading-relaxed">
            Spot a fraudulent listing or misleading claim? Write to{" "}
            <a href="mailto:sales@thepataudi.com" className="text-primary underline">sales@thepataudi.com</a> or WhatsApp{" "}
            <a href="https://wa.me/919467458749" target="_blank" rel="noopener noreferrer" className="text-primary underline">+91 9467458749</a>.
            We usually take action within a few hours.
          </p>
        </article>
      </main>
      <Footer />
    </div>
  );
}
